﻿import { Check, Loader2, MapPin, Pencil, Search, X } from "lucide-react";
import { Suspense, lazy, useCallback, useEffect, useId, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useLocale, useT } from "@/lib/i18n";

/**
 * Haritadan konum seçici — eski Biletzero projesindeki Google Maps akışının
 * Leaflet + OpenStreetMap/Nominatim karşılığı. Key/billing gerektirmez.
 *
 * UX akışı:
 *   • Varsayılan: kapalı panel. Mevcut seçim (varsa) lat/lon + adres özeti olarak görünür.
 *     "Haritadan Seç" / "Değiştir" butonu panel'i açar.
 *   • Açık: search + interaktif harita. Seçimler lokal draft state'te tutulur, parent'a
 *     yansımaz — yani yanlış tıklama formu kirletmez.
 *   • "Onayla" → draft parent'a iletilir, panel kapanır.
 *   • "Vazgeç" → draft atılır, panel kapanır; önceki seçim korunur.
 *
 * Nominatim rate limit: 1 req/s; search 500ms debounced.
 *
 * Inner map component Leaflet'i import ettiği için `next/dynamic` + `ssr: false`
 * ile yükleniyor — Leaflet module-level'da `window`'a referans alır.
 */
export interface LocationPickerProps {
  latitude: number | null;
  longitude: number | null;
  onChange: (
    coords: {
      latitude: number;
      longitude: number;
      address?: string | null;
      city?: string | null;
    } | null,
  ) => void;
  initialCenter?: { latitude: number; longitude: number };
  countryCode?: string;
  language?: string;
}

interface SearchResult {
  displayName: string;
  lat: number;
  lon: number;
  city: string | null;
}

/**
 * Nominatim address object'inden şehri çıkar. Türkiye'de `province` (il) tipik olarak
 * şehir; yoksa `city` veya `town` fallback. Bazı mahalleli sonuçlarda city yok ise
 * province kullanılır.
 */
function extractCity(addr: Record<string, string | undefined> | undefined): string | null {
  if (!addr) return null;
  return (
    addr.province ??
    addr.city ??
    addr.town ??
    addr.state ??
    addr.county ??
    null
  );
}

const DEFAULT_CENTER = { latitude: 41.0082, longitude: 28.9784 }; // İstanbul
const NOMINATIM_BASE = "https://nominatim.openstreetmap.org";

// Leaflet module-level olarak `window`'a referans alır → bundle splitting + lazy
// load ile ilk paint'i kirletmiyoruz. Vite SPA'de SSR yok, ama bu hâlâ
// route-level code-splitting için faydalı (haritalı sayfa açılmadan ~140KB
// Leaflet bundle ana chunk'a girmez).
const LocationPickerInner = lazy(() => import("./location-picker-inner"));

const InnerLoadingFallback = (
  <div className="flex h-[350px] w-full items-center justify-center bg-muted/40">
    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
  </div>
);

interface Draft {
  lat: number;
  lon: number;
  address: string | null;
  city: string | null;
}

export function LocationPicker({
  latitude,
  longitude,
  onChange,
  initialCenter = DEFAULT_CENTER,
  countryCode = "tr",
  language: languageProp,
}: LocationPickerProps) {
  const t = useT("locationPicker");
  const locale = useLocale();
  const language = languageProp ?? locale;
  const searchId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [searchFailed, setSearchFailed] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const hasSelection = latitude != null && longitude != null;

  const runSearch = useCallback(
    async (q: string) => {
      if (q.trim().length < 3) {
        setResults([]);
        setShowResults(false);
        return;
      }
      abortRef.current?.abort();
      const ctl = new AbortController();
      abortRef.current = ctl;
      setIsSearching(true);
      setSearchFailed(false);
      try {
        const url = new URL(`${NOMINATIM_BASE}/search`);
        url.searchParams.set("q", q);
        url.searchParams.set("format", "json");
        url.searchParams.set("limit", "5");
        url.searchParams.set("addressdetails", "1");
        if (countryCode) url.searchParams.set("countrycodes", countryCode);
        const resp = await fetch(url.toString(), {
          signal: ctl.signal,
          headers: { "Accept-Language": language },
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const json = (await resp.json()) as Array<{
          display_name: string;
          lat: string;
          lon: string;
          address?: Record<string, string | undefined>;
        }>;
        // Nominatim aynı POI'yi (aynı bina, farklı cepheli sokaklar) birden
        // fazla kez dönebiliyor. Ana isim + ~100m koordinat bazında dedup.
        const seen = new Set<string>();
        const deduped: SearchResult[] = [];
        for (const r of json) {
          const lat = Number(r.lat);
          const lon = Number(r.lon);
          const mainName = r.display_name.split(",")[0]?.trim().toLocaleLowerCase("tr-TR") ?? "";
          const key = `${mainName}|${lat.toFixed(3)}|${lon.toFixed(3)}`;
          if (seen.has(key)) continue;
          seen.add(key);
          deduped.push({
            displayName: r.display_name,
            lat,
            lon,
            city: extractCity(r.address),
          });
        }
        setResults(deduped);
        setShowResults(true);
      } catch (err) {
        if ((err as Error).name === "AbortError") return;
        setSearchFailed(true);
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    },
    [countryCode, language],
  );

  useEffect(() => {
    if (!isOpen) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, runSearch, isOpen]);

  const reverseGeocode = useCallback(
    async (lat: number, lon: number): Promise<{ address: string | null; city: string | null }> => {
      try {
        const url = new URL(`${NOMINATIM_BASE}/reverse`);
        url.searchParams.set("lat", String(lat));
        url.searchParams.set("lon", String(lon));
        url.searchParams.set("format", "json");
        url.searchParams.set("addressdetails", "1");
        const resp = await fetch(url.toString(), {
          headers: { "Accept-Language": language },
        });
        if (!resp.ok) return { address: null, city: null };
        const json = (await resp.json()) as {
          display_name?: string;
          address?: Record<string, string | undefined>;
        };
        return {
          address: json.display_name ?? null,
          city: extractCity(json.address),
        };
      } catch {
        return { address: null, city: null };
      }
    },
    [language],
  );

  const handleMapClick = useCallback(
    async (lat: number, lon: number) => {
      const { address, city } = await reverseGeocode(lat, lon);
      setDraft({ lat, lon, address, city });
    },
    [reverseGeocode],
  );

  const handleResultPick = (r: SearchResult) => {
    setShowResults(false);
    setQuery(r.displayName);
    setDraft({ lat: r.lat, lon: r.lon, address: r.displayName, city: r.city });
  };

  const openPicker = () => {
    // Mevcut seçimi draft'a yükle → panel açılınca marker zaten görünsün.
    setDraft(
      hasSelection && latitude != null && longitude != null
        ? { lat: latitude, lon: longitude, address: null, city: null }
        : null,
    );
    setQuery("");
    setResults([]);
    setSearchFailed(false);
    setIsOpen(true);
  };

  const cancelPicker = () => {
    setIsOpen(false);
    setDraft(null);
    setShowResults(false);
  };

  const confirmPicker = () => {
    if (draft) {
      onChange({
        latitude: draft.lat,
        longitude: draft.lon,
        address: draft.address,
        city: draft.city,
      });
    }
    setIsOpen(false);
    setDraft(null);
    setShowResults(false);
  };

  const clearSelection = () => {
    onChange(null);
  };

  // Açıkken haritanın merkezi: draft varsa draft, yoksa mevcut seçim, yoksa initialCenter.
  const mapCenter = draft
    ? { latitude: draft.lat, longitude: draft.lon }
    : hasSelection && latitude != null && longitude != null
      ? { latitude, longitude }
      : initialCenter;

  const mapMarker = draft ? { latitude: draft.lat, longitude: draft.lon } : null;

  // — Kapalı görünüm —
  // Mobile-first: icon+metin üstte, aksiyonlar altta tam genişlik. sm+ üzerinde tek satır.
  if (!isOpen) {
    return (
      <div className="rounded-md border bg-card p-3">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="flex min-w-0 items-start gap-3 sm:flex-1">
            <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </div>
            <div className="min-w-0 flex-1">
              {hasSelection && latitude != null && longitude != null ? (
                <>
                  <p className="text-sm font-medium">{t("selected")}</p>
                  <p className="mt-0.5 break-all text-xs text-muted-foreground" dir="ltr">
                    {latitude.toFixed(6)}, {longitude.toFixed(6)}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-sm font-medium">{t("notSelected")}</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{t("hint")}</p>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2 sm:shrink-0">
            {hasSelection ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearSelection}
                className="flex-1 sm:flex-none"
              >
                <X className="me-1 h-3.5 w-3.5" /> {t("clear")}
              </Button>
            ) : null}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={openPicker}
              className="flex-1 sm:flex-none"
            >
              {hasSelection ? (
                <>
                  <Pencil className="me-1 h-3.5 w-3.5" /> {t("change")}
                </>
              ) : (
                <>
                  <MapPin className="me-1 h-3.5 w-3.5" /> {t("pick")}
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // — Açık görünüm —
  return (
    <div className="space-y-3 rounded-md border bg-card p-3">
      <div className="relative">
        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            id={searchId}
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("searchPlaceholder")}
            className="ps-9 pe-9"
            onFocus={() => results.length > 0 && setShowResults(true)}
            onBlur={() => setTimeout(() => setShowResults(false), 150)}
          />
          {isSearching ? (
            <Loader2 className="absolute end-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
          ) : null}
        </div>

        {showResults && results.length > 0 ? (
          <ul className="absolute z-[1000] mt-1 max-h-64 w-full overflow-auto rounded-md border bg-popover p-1 text-sm shadow-lg">
            {results.map((r, i) => (
              <li key={`${r.lat}-${r.lon}-${i}`}>
                <button
                  type="button"
                  className="flex w-full items-start gap-2 rounded px-2 py-2 text-start hover:bg-accent"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleResultPick(r);
                  }}
                >
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="line-clamp-2">{r.displayName}</span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="overflow-hidden rounded-md border">
        <Suspense fallback={InnerLoadingFallback}>
          <LocationPickerInner center={mapCenter} marker={mapMarker} onMapClick={handleMapClick} />
        </Suspense>
      </div>

      {draft ? (
        <div className="rounded-md border bg-muted/30 px-3 py-2 text-xs">
          <div className="flex items-center gap-2 text-muted-foreground">
            <MapPin className="h-3.5 w-3.5" />
            <span className="font-medium" dir="ltr">
              {draft.lat.toFixed(6)}, {draft.lon.toFixed(6)}
            </span>
          </div>
          {draft.address ? <p className="mt-1 line-clamp-2 text-foreground">{draft.address}</p> : null}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t("mapHint")}</p>
      )}

      {searchFailed ? <p className="text-xs text-destructive">{t("searchFailed")}</p> : null}

      <div className="flex items-center justify-end gap-2 border-t pt-3">
        <Button type="button" variant="ghost" size="sm" onClick={cancelPicker}>
          {t("cancel")}
        </Button>
        <Button type="button" size="sm" onClick={confirmPicker} disabled={!draft}>
          <Check className="me-1 h-3.5 w-3.5" /> {t("confirm")}
        </Button>
      </div>
    </div>
  );
}
